import { Platform } from '@angular/cdk/platform';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
  Inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { enGB } from 'date-fns/locale';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';

import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { DATE_FORMAT_FOR_DATEPICKER } from './material-date-format';
import { RouteNames } from '@nx-ng/constants';

@Component({
  selector: 'nx-ng-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: DATE_FORMAT_FOR_DATEPICKER,
    },
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatepickerComponent),
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: enGB,
    },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent implements ControlValueAccessor, OnInit {
  @Input()
  date: Date | null = null;

  @Input()
  minDate: Date | null = null;

  @Input()
  maxDate: Date | null = null;

  @Input()
  startView: 'month' | 'year' | 'multi-year' = 'month';

  @Output()
  dateChanged: EventEmitter<Date | null> = new EventEmitter<Date | null>();

  @Input()
  id = '';

  @Input()
  label = '';

  @Input()
  placeholder = 'date_placeholder';

  @Input()
  errorText = '';

  @Input()
  isRequired = false;

  private localValue: Date | null = null;
  private touched = false;

  constructor(
    @Inject(I18NEXT_SERVICE) private i18n: ITranslationService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.writeValue(this.date);
  }

  set value(value: Date | null) {
    value?.setHours(2);
    this.localValue = value;
    this.dateChanged.emit(value);
    this.onChange(value);

    setTimeout(() => {
      const element = document?.activeElement as HTMLInputElement;
      if (element?.id !== this.id) {
        this.markAsTouched();
      }
    }, 500);
  }

  get value(): Date | null {
    return this.localValue;
  }

  onChange = (value: Date | null) => {};

  onTouch = () => {};

  writeValue(value: Date | null) {
    this.localValue = value;
    if (this.changeDetectorRef) {
      this.changeDetectorRef.markForCheck();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  get errorId() {
    return `${this.id}-error`;
  }

  get hasError() {
    return this.errorText?.length > 0;
  }

  markAsTouched() {
    if (!this.touched) {
      this.touched = true;
      this.onTouch();
    }
  }
}
